import { Form, Select } from 'antd'
import { FC, useEffect, useState } from 'react'
import { CustomFormInstance, ValueOf } from '../../../../types'
import {
    FormValues,
    MachineForm,
    MachineNames,
    MachineValues,
    PrimaryAdditionalComponent,
    PrimaryComponent,
    SubComponent,
} from '../types'
import { connectionRelation, UNITS } from '../../../../const'
import { extractComponentName } from '../../../functions'

type Props = {
    form: CustomFormInstance<MachineForm>
    name: string
}

const SummaryOfSelectedEmbodimentsOfTheInvention: FC<Props> = ({
    form,
    name,
}) => {
    type Option = {
        value: string
        label: string
        disabled?: boolean
    }
    const [selectOptions, setSelectOptions] = useState<Option[]>([])
    useEffect(() => {
        const primaryComponents =
            form.getFieldValue<MachineForm['primaryComponents']>(
                'primaryComponents'
            ) ?? []
        const additionalComponents = form.getFieldValue<
            MachineForm['primaryAdditionalComponents']
        >(MachineNames.primaryAdditionalComponents)
        const partsOfIndependentClaim = form.getFieldValue<
            MachineValues['partsOfIndependentClaim']
        >(['partsOfIndependentClaim'])
        const summaryOfSelectedEmbodimentsOfTheInvention = form.getFieldValue<
            FormValues['summaryOfSelectedEmbodimentsOfTheInvention']
        >(['summaryOfSelectedEmbodimentsOfTheInvention'])
        const subComponents = form.getFieldValue<
            MachineForm['primaryComponentSubComponentsList']
        >('primaryComponentSubComponentsList')

        // Add primary components
        setSelectOptions((prevState) =>
            prevState.concat(
                primaryComponents.map((comp: PrimaryComponent) => ({
                    label: comp.component ?? '',
                    value: comp.id ?? '',
                }))
            )
        )

        // Add additional components
        if (additionalComponents) {
            setSelectOptions((prevState) =>
                prevState.concat(
                    additionalComponents.map(
                        (addComp: PrimaryAdditionalComponent) => ({
                            label: addComp.component ?? '',
                            value: `add_${addComp.id}`,
                        })
                    )
                )
            )
        }

        // Add sub components
        if (subComponents) {
            primaryComponents.forEach((primaryComponent: PrimaryComponent) => {
                const subComponent = subComponents[primaryComponent.id ?? '']
                if (subComponent) {
                    setSelectOptions((prevState) =>
                        prevState.concat(
                            subComponent.map((subComp: SubComponent) => ({
                                label: primaryComponent.component
                                    ? `${subComp.component} from the ${primaryComponent.component} component`
                                    : subComp.component ?? '',
                                value: primaryComponent.id
                                    ? `${primaryComponent.id}+=>${subComp.id}`
                                    : subComp.id ?? '',
                                disabled: !partsOfIndependentClaim.includes(
                                    primaryComponent.id
                                ),
                            }))
                        )
                    )
                }
            })
        }
        const parentClaimPrimaryComponents = primaryComponents.filter(
            (item) => {
                if (
                    summaryOfSelectedEmbodimentsOfTheInvention &&
                    item !== undefined
                ) {
                    return summaryOfSelectedEmbodimentsOfTheInvention.includes(
                        item.id ?? ''
                    )
                }
                if (partsOfIndependentClaim && item !== undefined) {
                    return partsOfIndependentClaim.includes(item.id ?? '')
                }
                return item !== undefined
            }
        )
        const parentClaimAdditionalComponents = additionalComponents?.filter(
            (item) => {
                if (
                    summaryOfSelectedEmbodimentsOfTheInvention &&
                    item !== undefined
                ) {
                    return summaryOfSelectedEmbodimentsOfTheInvention.includes(
                        `add_${item.id}`
                    )
                }
                if (partsOfIndependentClaim && item !== undefined) {
                    return partsOfIndependentClaim.includes(`add_${item.id}`)
                }
                return false
            }
        )
        const characteristicComponents = form.getFieldValue(
            MachineNames.componentsForCharacteristics
        ) as MachineValues['componentsForCharacteristics']
        characteristicComponents.forEach((component: string) => {
            const foundComponent = primaryComponents.find(
                (x) => x?.id === component
            )
            const parentClaimPrimaryComponent =
                parentClaimPrimaryComponents.find((x) => x?.id === component)
            if (foundComponent) {
                const characteristic = form.getFieldValue([
                    MachineNames.characteristic,
                    component,
                ]) as ValueOf<MachineValues['characteristic']>
                const additionalCharacteristic = form.getFieldValue<
                    ValueOf<MachineValues['additionalCharacteristic']>
                >(['additionalCharacteristic', component])
                const formatUserDefined = form.getFieldValue<
                    ValueOf<MachineValues['formatUserDefined']>
                >(['formatUserDefined', component])
                if (characteristic) {
                    Object.entries(characteristic).forEach((entity) => {
                        const [entityKey, val] = entity
                        const key = entityKey as keyof typeof UNITS
                        if (val) {
                            if (typeof val === 'object' && val !== null) {
                                if (key === 'defined') {
                                    setSelectOptions((prevState) =>
                                        prevState.concat([
                                            {
                                                label: `${
                                                    'description' in val
                                                        ? val.description
                                                        : '__'
                                                }`,
                                                value: `characteristic_${component}.${key}`,
                                                disabled:
                                                    !parentClaimPrimaryComponent,
                                            },
                                        ])
                                    )
                                } else {
                                    setSelectOptions((prevState) =>
                                        prevState.concat([
                                            {
                                                label: `${foundComponent.component!} has a ${key} from ${
                                                    'from' in val
                                                        ? val.from
                                                        : '__'
                                                } to ${
                                                    'to' in val ? val.to : '__'
                                                } ${UNITS[key]}`,
                                                value: `characteristic_${component}.${key}`,
                                                disabled:
                                                    !parentClaimPrimaryComponent,
                                            },
                                        ])
                                    )
                                }
                            }
                        }
                    })
                }
                if (additionalCharacteristic) {
                    Object.entries(additionalCharacteristic).forEach(
                        (entity) => {
                            const [entityKey, val] = entity
                            const key = entityKey as keyof typeof UNITS
                            if (val) {
                                if (typeof val === 'object' && val !== null) {
                                    if ('description' in val) {
                                        if (key === 'defined') {
                                            setSelectOptions((prevState) =>
                                                prevState.concat([
                                                    {
                                                        label: `${
                                                            'description' in val
                                                                ? val.description
                                                                : '__'
                                                        } `,
                                                        value: `additionalCharacteristic_${component}.${key}`,
                                                        disabled:
                                                            !parentClaimPrimaryComponent,
                                                    },
                                                ])
                                            )
                                        }
                                    } else {
                                        if (key === 'thermalConductivity') {
                                            setSelectOptions((prevState) =>
                                                prevState.concat([
                                                    {
                                                        label: `${
                                                            foundComponent.component
                                                        } has a thermal conductivity from ${
                                                            val.from || '__'
                                                        } to ${
                                                            val.to || '__'
                                                        } ${UNITS[key]}`,
                                                        value: `additionalCharacteristic_${component}.${key}`,
                                                        disabled:
                                                            !parentClaimPrimaryComponent,
                                                    },
                                                ])
                                            )
                                        }
                                        if (key === 'meltingPoint') {
                                            setSelectOptions((prevState) =>
                                                prevState.concat([
                                                    {
                                                        label: `${
                                                            foundComponent.component
                                                        } has a melting point from ${
                                                            val.from || '__'
                                                        } to ${
                                                            val.to || '__'
                                                        } ${UNITS[key]}`,
                                                        value: `additionalCharacteristic_${component}.${key}`,
                                                        disabled:
                                                            !parentClaimPrimaryComponent,
                                                    },
                                                ])
                                            )
                                        }
                                        if (key === 'specificHeat') {
                                            setSelectOptions((prevState) =>
                                                prevState.concat([
                                                    {
                                                        label: `${
                                                            foundComponent.component
                                                        } has a specific heat from ${
                                                            val.from || '__'
                                                        } to ${
                                                            val.to || '__'
                                                        } ${UNITS[key]}`,
                                                        value: `additionalCharacteristic_${component}.${key}`,
                                                        disabled:
                                                            !parentClaimPrimaryComponent,
                                                    },
                                                ])
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    )
                }
                if (formatUserDefined) {
                    Object.entries(formatUserDefined).forEach((entity: any) => {
                        const [entityKey, val] = entity
                        const key = entityKey
                        if (val) {
                            if (typeof val === 'object' && val !== null) {
                                if (key === 'from') {
                                    setSelectOptions((prevState) =>
                                        prevState.concat([
                                            {
                                                label: `${foundComponent.component!} has a ${
                                                    val.name || '__'
                                                } from ${
                                                    val.minimum || '__'
                                                } to ${val.maximum || '__'} ${
                                                    val.units || ''
                                                }`,
                                                value: `formatUserDefined_${component}.${key}`,
                                                disabled:
                                                    !parentClaimPrimaryComponent,
                                            },
                                        ])
                                    )
                                }
                                if (key === 'equal') {
                                    setSelectOptions((prevState) =>
                                        prevState.concat([
                                            {
                                                label: `${
                                                    val.name || '__'
                                                } of the ${foundComponent.component!} is equal to from ${
                                                    val.minimum || '__'
                                                } to ${val.maximum || '__'} ${
                                                    val.units || ''
                                                }`,
                                                value: `formatUserDefined_${component}.${key}`,
                                                disabled:
                                                    !parentClaimPrimaryComponent,
                                            },
                                        ])
                                    )
                                }
                                if (key === 'defined') {
                                    setSelectOptions((prevState) =>
                                        prevState.concat([
                                            {
                                                label: `${
                                                    val.description || '___'
                                                }`,
                                                value: `formatUserDefined_${component}.${key}`,
                                                disabled:
                                                    !parentClaimPrimaryComponent,
                                            },
                                        ])
                                    )
                                }
                            }
                        }
                    })
                }
            }
        })

        if (parentClaimAdditionalComponents) {
            const additionalComponentsForCharacteristics = form.getFieldValue<
                MachineValues['additionalComponentsForCharacteristics']
            >('additionalComponentsForCharacteristics')

            additionalComponentsForCharacteristics?.forEach(
                (component: string) => {
                    const foundComponent = additionalComponents?.find(
                        (x) => x?.id === component
                    )
                    const parentClaimAdditionalComponent =
                        parentClaimAdditionalComponents?.find(
                            (x) => x?.id === component
                        )
                    if (foundComponent) {
                        const addCompCharacteristic = form.getFieldValue([
                            MachineNames.addCompCharacteristic,
                            component,
                        ]) as ValueOf<MachineValues['addCompCharacteristic']>
                        const addCompAdditionalCharacteristic =
                            form.getFieldValue<
                                ValueOf<
                                    MachineValues['addCompAdditionalCharacteristic']
                                >
                            >(['addCompAdditionalCharacteristic', component])
                        const addCompFormatUserDefined = form.getFieldValue<
                            ValueOf<MachineValues['addCompFormatUserDefined']>
                        >(['addCompFormatUserDefined', component])
                        if (addCompCharacteristic) {
                            Object.entries(addCompCharacteristic).forEach(
                                (entity) => {
                                    const [entityKey, val] = entity
                                    const key = entityKey as keyof typeof UNITS
                                    if (val) {
                                        if (
                                            typeof val === 'object' &&
                                            val !== null
                                        ) {
                                            if (key === 'defined') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                'description' in
                                                                val
                                                                    ? val.description
                                                                    : '__'
                                                            }`,
                                                            value: `addCompCharacteristic_${component}.${key}`,
                                                            disabled:
                                                                !parentClaimAdditionalComponent,
                                                        },
                                                    ])
                                                )
                                            } else {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${foundComponent.component!} has a ${key} from ${
                                                                'from' in val
                                                                    ? val.from
                                                                    : '__'
                                                            } to ${
                                                                'to' in val
                                                                    ? val.to
                                                                    : '__'
                                                            } ${UNITS[key]}`,
                                                            value: `addCompCharacteristic_${component}.${key}`,
                                                            disabled:
                                                                !parentClaimAdditionalComponent,
                                                        },
                                                    ])
                                                )
                                            }
                                        }
                                    }
                                }
                            )
                        }
                        if (addCompAdditionalCharacteristic) {
                            Object.entries(
                                addCompAdditionalCharacteristic
                            ).forEach((entity) => {
                                const [entityKey, val] = entity
                                const key = entityKey as keyof typeof UNITS
                                if (val) {
                                    if (
                                        typeof val === 'object' &&
                                        val !== null
                                    ) {
                                        if ('description' in val) {
                                            if (key === 'defined') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                'description' in
                                                                val
                                                                    ? val.description
                                                                    : '__'
                                                            } `,
                                                            value: `addCompAdditionalCharacteristic_${component}.${key}`,
                                                            disabled:
                                                                !parentClaimAdditionalComponent,
                                                        },
                                                    ])
                                                )
                                            }
                                        } else {
                                            if (key === 'thermalConductivity') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                foundComponent.component
                                                            } has a thermal conductivity from ${
                                                                val.from || '__'
                                                            } to ${
                                                                val.to || '__'
                                                            } ${UNITS[key]}`,
                                                            value: `addCompAdditionalCharacteristic_${component}.${key}`,
                                                            disabled:
                                                                !parentClaimAdditionalComponent,
                                                        },
                                                    ])
                                                )
                                            }
                                            if (key === 'meltingPoint') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                foundComponent.component
                                                            } has a melting point from ${
                                                                val.from || '__'
                                                            } to ${
                                                                val.to || '__'
                                                            } ${UNITS[key]}`,
                                                            value: `addCompAdditionalCharacteristic_${component}.${key}`,
                                                            disabled:
                                                                !parentClaimAdditionalComponent,
                                                        },
                                                    ])
                                                )
                                            }
                                            if (key === 'specificHeat') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                foundComponent.component
                                                            } has a specific heat from ${
                                                                val.from || '__'
                                                            } to ${
                                                                val.to || '__'
                                                            } ${UNITS[key]}`,
                                                            value: `addCompAdditionalCharacteristic_${component}.${key}`,
                                                            disabled:
                                                                !parentClaimAdditionalComponent,
                                                        },
                                                    ])
                                                )
                                            }
                                        }
                                    }
                                }
                            })
                        }
                        if (addCompFormatUserDefined) {
                            Object.entries(addCompFormatUserDefined).forEach(
                                (entity: any) => {
                                    const [entityKey, val] = entity
                                    const key = entityKey
                                    if (val) {
                                        if (
                                            typeof val === 'object' &&
                                            val !== null
                                        ) {
                                            if (key === 'from') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${foundComponent.component!} has a ${
                                                                val.name || '__'
                                                            } from ${
                                                                val.minimum ||
                                                                '__'
                                                            } to ${
                                                                val.maximum ||
                                                                '__'
                                                            } ${
                                                                val.units || ''
                                                            }`,
                                                            value: `addCompFormatUserDefined_${component}.${key}`,
                                                            disabled:
                                                                !parentClaimAdditionalComponent,
                                                        },
                                                    ])
                                                )
                                            }
                                            if (key === 'equal') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                val.name || '__'
                                                            } of the ${foundComponent.component!} is equal to from ${
                                                                val.minimum ||
                                                                '__'
                                                            } to ${
                                                                val.maximum ||
                                                                '__'
                                                            } ${
                                                                val.units || ''
                                                            }`,
                                                            value: `addCompFormatUserDefined_${component}.${key}`,
                                                            disabled:
                                                                !parentClaimAdditionalComponent,
                                                        },
                                                    ])
                                                )
                                            }
                                            if (key === 'defined') {
                                                setSelectOptions((prevState) =>
                                                    prevState.concat([
                                                        {
                                                            label: `${
                                                                val.description ||
                                                                '___'
                                                            }`,
                                                            value: `addCompFormatUserDefined_${component}.${key}`,
                                                            disabled:
                                                                !parentClaimAdditionalComponent,
                                                        },
                                                    ])
                                                )
                                            }
                                        }
                                    }
                                }
                            )
                        }
                    }
                }
            )
        }
        const importantComponentsConnections = form.getFieldValue<
            MachineValues['importantComponentsConnections']
        >(['importantComponentsConnections'])
        if (importantComponentsConnections != null) {
            Object.entries(importantComponentsConnections).forEach(
                (entry: any) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const [key, val] = entry
                    let finalText: string | undefined
                    const notSet = val?.notSet
                    const firstComponent =
                        val &&
                        ((partsOfIndependentClaim &&
                            partsOfIndependentClaim.includes(
                                val['1stComponent']
                            )) ||
                            (summaryOfSelectedEmbodimentsOfTheInvention &&
                                summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                    val['1stComponent']
                                ))) &&
                        extractComponentName(val['1stComponent'], form)
                    const secondComponent =
                        val &&
                        ((partsOfIndependentClaim &&
                            partsOfIndependentClaim.includes(
                                val['2ndComponent']
                            )) ||
                            (summaryOfSelectedEmbodimentsOfTheInvention &&
                                summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                    val['2ndComponent']
                                ))) &&
                        extractComponentName(val['2ndComponent'], form)
                    if (notSet) {
                        const firstPart = val?.['1stPart']
                        const secondPart = val?.['2ndPart']
                        const thirdPart = val?.['3rdPart']
                        if (
                            firstPart &&
                            firstComponent &&
                            secondPart &&
                            secondComponent &&
                            thirdPart
                        ) {
                            finalText = `${firstPart} the ${firstComponent} ${secondPart} the ${secondComponent} ${
                                thirdPart || '__'
                            }`
                        }
                    } else {
                        const foundSubConnectionType = connectionRelation[
                            val?.connectionType
                        ]?.find((connection: any) => {
                            return connection.value === val?.subConnectionType
                        })
                        const connectionType = foundSubConnectionType
                            ? val?.subConnectionType || val?.connectionType
                            : val?.connectionType
                        if (
                            firstComponent &&
                            connectionType &&
                            secondComponent
                        ) {
                            finalText = `${firstComponent} is ${connectionType.toLowerCase()} the ${secondComponent}`
                        }
                    }
                    if (finalText) {
                        setSelectOptions((prevState) =>
                            prevState.concat([
                                {
                                    label: `${finalText}`,
                                    value: `importantComponentsConnections_${val?.id}`,
                                },
                            ])
                        )
                    }
                }
            )
        }
        const importantComponentsArrangements = form.getFieldValue<
            MachineValues['importantComponentsArrangements']
        >(['importantComponentsArrangements'])
        if (importantComponentsArrangements != null) {
            Object.entries(importantComponentsArrangements).forEach(
                (entry: any) => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const [key, val] = entry
                    let finalText: string | undefined
                    const notSet = val?.notSet
                    const firstComponent =
                        val &&
                        ((partsOfIndependentClaim &&
                            partsOfIndependentClaim.includes(
                                val['1stComponent']
                            )) ||
                            (summaryOfSelectedEmbodimentsOfTheInvention &&
                                summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                    val['1stComponent']
                                ))) &&
                        extractComponentName(val['1stComponent'], form)
                    const secondComponent =
                        val &&
                        ((partsOfIndependentClaim &&
                            partsOfIndependentClaim.includes(
                                val['2ndComponent']
                            )) ||
                            (summaryOfSelectedEmbodimentsOfTheInvention &&
                                summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                    val['2ndComponent']
                                ))) &&
                        extractComponentName(val['2ndComponent'], form)
                    if (notSet) {
                        const firstPart = val?.['1stPart']
                        const secondPart = val?.['2ndPart']
                        const thirdPart = val?.['3rdPart']
                        if (firstComponent && secondPart && secondComponent) {
                            finalText = `${
                                firstPart ? `${firstPart} the ` : ''
                            }${firstComponent} ${secondPart} the ${secondComponent} ${
                                thirdPart || '__'
                            }`
                        }
                    } else {
                        const arrangementType = val?.arrangementType
                        if (
                            firstComponent &&
                            arrangementType &&
                            secondComponent
                        ) {
                            finalText = `${firstComponent} is ${arrangementType.toLowerCase()} the ${secondComponent}`
                        }
                    }
                    if (finalText) {
                        setSelectOptions((prevState) =>
                            prevState.concat([
                                {
                                    label: `${finalText}`,
                                    value: `importantComponentsArrangements_${val?.id}`,
                                },
                            ])
                        )
                    }
                }
            )
        }
        const functionalConnections = form.getFieldValue<
            MachineValues['functionalConnections']
        >(['functionalConnections'])
        if (functionalConnections != null) {
            Object.entries(functionalConnections).forEach((entry: any) => {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const [key, val] = entry
                let finalText: string | undefined
                const notSet = val?.notSet
                const firstComponent =
                    val &&
                    ((partsOfIndependentClaim &&
                        partsOfIndependentClaim.includes(
                            val['1stComponent']
                        )) ||
                        (summaryOfSelectedEmbodimentsOfTheInvention &&
                            summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                val['1stComponent']
                            ))) &&
                    extractComponentName(val['1stComponent'], form)
                const secondComponent =
                    val &&
                    ((partsOfIndependentClaim &&
                        partsOfIndependentClaim.includes(
                            val['2ndComponent']
                        )) ||
                        (summaryOfSelectedEmbodimentsOfTheInvention &&
                            summaryOfSelectedEmbodimentsOfTheInvention.includes(
                                val['2ndComponent']
                            ))) &&
                    extractComponentName(val['2ndComponent'], form)
                if (notSet) {
                    const firstPart = val?.['1stPart']
                    const secondPart = val?.['2ndPart']
                    const thirdPart = val?.['3rdPart']
                    if (firstComponent && secondPart && secondComponent) {
                        finalText = `${
                            firstPart ? `${firstPart} the ` : ''
                        }${firstComponent} ${secondPart} the ${secondComponent} ${
                            thirdPart || '__'
                        }`
                    }
                } else if (firstComponent && secondComponent) {
                    finalText = `${firstComponent} is functionally connected with the ${secondComponent}`
                }
                if (finalText) {
                    setSelectOptions((prevState) =>
                        prevState.concat([
                            {
                                label: `${finalText}`,
                                value: `functionalConnections_${val?.id}`,
                            },
                        ])
                    )
                }
            })
        }
    }, [form])
    const handleChange = (event: any) => {
        setSelectOptions((prevState) =>
            prevState.map((option) => {
                if (option.value.includes('=>')) {
                    if (!event.includes(option.value.split('+=>')[0])) {
                        return { ...option, disabled: true }
                    }
                    return { ...option, disabled: false }
                }

                if (
                    option.value.includes('characteristic_') ||
                    option.value.includes('additionalCharacteristic_') ||
                    option.value.includes('formatUserDefined_')
                ) {
                    if (
                        !event.includes(
                            option.value.split('_')[1].split('.')[0]
                        )
                    ) {
                        return { ...option, disabled: true }
                    }
                    return { ...option, disabled: false }
                }

                if (
                    option.value.includes('addCompCharacteristic_') ||
                    option.value.includes('addCompAdditionalCharacteristic_') ||
                    option.value.includes('addCompFormatUserDefined_')
                ) {
                    if (
                        !event.includes(
                            `add_${option.value.split('_')[1].split('.')[0]}`
                        )
                    ) {
                        return { ...option, disabled: true }
                    }
                    return { ...option, disabled: false }
                }

                if (option.value.includes('importantComponentsConnections_')) {
                    const importantComponentsConnections = form.getFieldValue<
                        MachineForm['importantComponentsConnections']
                    >(['importantComponentsConnections'])
                    const foundImportantComponentsConnection =
                        importantComponentsConnections.find(
                            (connection) =>
                                connection?.id === option.value.split('_')[1]
                        )
                    if (foundImportantComponentsConnection) {
                        if (
                            !event.includes(
                                foundImportantComponentsConnection[
                                    '1stComponent'
                                ]
                            ) ||
                            !event.includes(
                                foundImportantComponentsConnection[
                                    '2ndComponent'
                                ]
                            )
                        ) {
                            return { ...option, disabled: true }
                        }
                        return { ...option, disabled: false }
                    }
                    return { ...option, disabled: true }
                }

                if (option.value.includes('importantComponentsArrangements_')) {
                    const importantComponentsArrangements = form.getFieldValue<
                        MachineForm['importantComponentsArrangements']
                    >(['importantComponentsArrangements'])
                    const foundImportantComponentsArrangement =
                        importantComponentsArrangements.find(
                            (connection) =>
                                connection?.id === option.value.split('_')[1]
                        )
                    if (foundImportantComponentsArrangement) {
                        if (
                            !event.includes(
                                foundImportantComponentsArrangement[
                                    '1stComponent'
                                ]
                            ) ||
                            !event.includes(
                                foundImportantComponentsArrangement[
                                    '2ndComponent'
                                ]
                            )
                        ) {
                            return { ...option, disabled: true }
                        }
                        return { ...option, disabled: false }
                    }
                    return { ...option, disabled: true }
                }

                if (option.value.includes('functionalConnections_')) {
                    const functionalConnections = form.getFieldValue<
                        MachineForm['functionalConnections']
                    >(['functionalConnections'])
                    const foundFunctionalConnection =
                        functionalConnections.find(
                            (connection) =>
                                connection?.id === option.value.split('_')[1]
                        )
                    if (foundFunctionalConnection) {
                        if (
                            !event.includes(
                                foundFunctionalConnection['1stComponent']
                            ) ||
                            !event.includes(
                                foundFunctionalConnection['2ndComponent']
                            )
                        ) {
                            return { ...option, disabled: true }
                        }
                        return { ...option, disabled: false }
                    }
                    return { ...option, disabled: true }
                }

                return option
            })
        )
        const filteredValue = event.filter((item: string) => {
            if (item.includes('+=>')) {
                return event.includes(item.split('+=>')[0])
            }
            if (
                item.includes('characteristic_') ||
                item.includes('additionalCharacteristic_') ||
                item.includes('formatUserDefined_')
            ) {
                return event.includes(item.split('_')[1].split('.')[0])
            }
            if (
                item.includes('addCompCharacteristic_') ||
                item.includes('addCompAdditionalCharacteristic_') ||
                item.includes('addCompFormatUserDefined_')
            ) {
                return event.includes(`add_${item.split('_')[1].split('.')[0]}`)
            }
            if (item.includes('importantComponentsConnections_')) {
                const importantComponentsConnections = form.getFieldValue<
                    MachineForm['importantComponentsConnections']
                >(['importantComponentsConnections'])
                const foundImportantComponentsConnection =
                    importantComponentsConnections.find(
                        (connection) => connection?.id === item.split('_')[1]
                    )
                return (
                    foundImportantComponentsConnection &&
                    event.includes(
                        foundImportantComponentsConnection['1stComponent']
                    ) &&
                    event.includes(
                        foundImportantComponentsConnection['2ndComponent']
                    )
                )
            }
            if (item.includes('importantComponentsArrangements_')) {
                const importantComponentsArrangements = form.getFieldValue<
                    MachineForm['importantComponentsArrangements']
                >(['importantComponentsArrangements'])
                const foundImportantComponentsArrangement =
                    importantComponentsArrangements.find(
                        (connection) => connection?.id === item.split('_')[1]
                    )
                return (
                    foundImportantComponentsArrangement &&
                    event.includes(
                        foundImportantComponentsArrangement['1stComponent']
                    ) &&
                    event.includes(
                        foundImportantComponentsArrangement['2ndComponent']
                    )
                )
            }
            if (item.includes('functionalConnections_')) {
                const functionalConnections = form.getFieldValue<
                    MachineForm['functionalConnections']
                >(['functionalConnections'])
                const foundFunctionalConnection = functionalConnections.find(
                    (connection) => connection?.id === item.split('_')[1]
                )
                return (
                    foundFunctionalConnection &&
                    event.includes(foundFunctionalConnection['1stComponent']) &&
                    event.includes(foundFunctionalConnection['2ndComponent'])
                )
            }

            return true
        })

        form.setFieldsValue({
            [name]: filteredValue,
        })
    }
    return (
        <>
            <Form.Item
                name={name}
                rules={[
                    {
                        required: true,
                        message: 'This field is required!',
                    },
                ]}
                initialValue={form
                    .getFieldValue<MachineValues['partsOfIndependentClaim']>([
                        'partsOfIndependentClaim',
                    ])
                    .concat(
                        form.getFieldValue<
                            MachineValues['featuresPartsOfIndependentClaim']
                        >(['featuresPartsOfIndependentClaim']) ?? []
                    )}
            >
                <Select
                    mode="multiple"
                    onChange={handleChange}
                    dropdownClassName="always-visible-scrollbar-dropdown"
                    options={selectOptions.map((option: Option) => {
                        return {
                            value: option.value,
                            label: option.label,
                            disabled: option.disabled ?? false,
                        }
                    })}
                />
            </Form.Item>
        </>
    )
}

export default SummaryOfSelectedEmbodimentsOfTheInvention
